import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  InputGroup,
} from "reactstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { put } from "../../helpers/api_helper";
import { CUSTOMER_UPDATE } from "../../helpers/url_helper";
import toastr from "toastr";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const EditCustomerModal = ({
  isOpen,
  toggle,
  customerData,
  onUpdated,
  isAdmin,
  isAccountant,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      phone_no: "",
      name: "",
      Discount: "",
      notes: "",
      email: "",
      status: "1",
      address: "",
    },
  });
  const [creditDateMessage, setCreditDateMessage] = useState('')
  const [debitDateMessage, setDebitDateMessage] = useState('')
  const [customerUlId, setCustomerUlId] = useState();
  const [debitMode, setDebitMode] = useState(false);
  const creditAmount = useWatch({
    control,
    name: "credit_amount",
  });
  const creditedDate = useWatch({
    control,
    name: 'credited_date'
  })
  useEffect(() => {
    setCreditDateMessage('')
  }, [creditAmount, creditedDate, setValue])

  useEffect(() => {
    if (customerData) {
      reset(customerData);
    }
  }, [customerData, reset]);

  const onSubmit = async (data) => {
    try {
      const changedFields = Object.keys(dirtyFields).reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});
      if (changedFields?.credit_balance && !changedFields?.credited_date) {
        setCreditDateMessage('Please select credited date.')
        return;
      }
      if (changedFields?.debit_balance && !changedFields?.debit_date) {
        setDebitDateMessage('Please select debited date.')
        return;
      }
      await put(CUSTOMER_UPDATE(customerData.ul_id), {phone: customerData.phone, ...changedFields }); // ULID is passed here
      toastr.success("Customer updated successfully!");
      toggle(); // Close modal
      onUpdated(); // Refresh list
    } catch (error) {
      console.error("Error updating customer", error);
      toastr.error("Failed to update customer.");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Edit Customer</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="phone">
                  Phone No <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: "Phone number is required",
                    pattern: {
                      value: /^[0-9]*$/, // only numbers
                      message: "Please enter only numbers",
                    },
                    minLength: {
                      value: 10,
                      message: "Must be 10-digits phone number",
                    },
                    maxLength: {
                      value: 10,
                      message: "Must be 10-digits phone number",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="phone"
                      placeholder="Enter Phone no"
                      type="tel" // opens phone's dial pad
                      inputMode="numeric" // fallback to show numeric keyboard
                      pattern="[0-9]*"
                      maxLength="10"
                      onKeyDown={(e) => {
                        // block non-digits
                        if (
                          !/[0-9]|Backspace|ArrowLeft|ArrowRight|Tab|Delete/.test(
                            e.key
                          )
                        ) {
                          e.preventDefault();
                        }
                      }}
                      className={`form-control ${
                        errors.phone ? "is-invalid" : ""
                      }`}
                      disabled={isAccountant}
                    />
                  )}
                />

                {errors.phone_no && (
                  <div className="text-danger">{errors.phone_no.message}</div>
                )}
              </div>
            </Col>
            <Col md="6" xl="4">
              <div className="mb-3 position-relative">
                <Label className="form-label" htmlFor="validationTooltip03">
                  Email
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                      message: "Invalid email format",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className={`form-control ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      id="email"
                      placeholder="Enter Email"
                      disabled={isAccountant}
                    />
                  )}
                />
                {errors.email?.message && (
                  <div className="text-danger">{errors.email.message}</div>
                )}
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="name">Name</Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      id="name"
                      placeholder="Enter name"
                      disabled={isAccountant}
                    />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="Discount">Discount</Label>
                <Controller
                  name="discount"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      placeholder="Enter Discount"
                    />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="Credit Balance">Credit Balance</Label>
                <Controller
                  name="credit_balance"
                  control={control}
                  render={({ field }) => (
                    <Input {...field} type="text" placeholder="Enter Balance" />
                  )}
                />
              </div>
            </Col>

            <Col md="6" xl="4">
              <div className="mb-3 position-relative">
                <Label className="form-label" htmlFor="validationTooltip02" > Credit Date </Label>
                <Controller
                  name="credited_date"
                  control={control}
                  render={({ field }) => (
                    <InputGroup>
                      <Flatpickr
                        options={{ dateFormat: "Y-m-d" }}
                        value={field.value || null}
                        onChange={(selectedDates) => {
                          if (selectedDates?.[0]) {
                            const formatted = selectedDates[0].toLocaleDateString("en-CA"); // yyyy-mm-dd
                            field.onChange(formatted);
                          }
                        }}
                        className="form-control"
                        placeholder="Select Date"
                      />
                    </InputGroup>
                  )}
                />
              {
                <div className="text-danger">{creditDateMessage}</div>
              }
              </div>
            </Col>
            <Col md="6" xl="4">
              <div className="mb-3">
                <Label htmlFor="customer_status">status</Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      type="select"
                      {...field}
                      id="customer_status"
                      className="form-select"
                      disabled={isAccountant}
                    >
                      <option value="">Select Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </Input>
                  )}
                />
              </div>
            </Col>
            <Col md="12" xl="8">
              <div className="mb-3">
                <Label htmlFor="remarks">Remarks</Label>

                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      placeholder="Enter notes"
                    />
                  )}
                />
              </div>
            </Col>
            <Col md="6" xl="4">
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                name="inputMode"
                checked={debitMode}
                onChange={(e) => setDebitMode(e.target.checked)} // Toggle on check/uncheck
                id="debitmode"
              />
              <Label className="form-check-label" htmlFor="debitmode">Debit Balance</Label>
            </div>
          </Col>

          {debitMode && (
              <>
                <Col md="6" xl="4">
                  <div className="mb-3">
                    <Label htmlFor="Credit Balance">Debit Balance</Label>
                    <Controller
                      name="debit_balance"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} type="text" placeholder="Enter Balance" />
                      )}
                    />
                  </div>
                </Col>
                <Col md="6" xl="4">
                  <div className="mb-3 position-relative">
                    <Label className="form-label" htmlFor="validationTooltip02" > Debit Date </Label>
                    <Controller
                      name="debit_date"
                      control={control}
                      render={({ field }) => (
                        <InputGroup>
                          <Flatpickr
                            options={{ dateFormat: "Y-m-d" }}
                            value={field.value || null}
                            onChange={(selectedDates) => {
                              if (selectedDates?.[0]) {
                                const formatted = selectedDates[0].toLocaleDateString("en-CA"); // yyyy-mm-dd
                                field.onChange(formatted);
                              }
                            }}
                            className="form-control"
                            placeholder="Select Date"
                          />
                        </InputGroup>
                      )}
                    />
                  {
                    <div className="text-danger">{debitDateMessage}</div>
                  }
                  </div>
                </Col>
              </>
          )}
          </Row>
          <div className="d-flex justify-content-end">
            <Button color="secondary" onClick={toggle} className="me-2">
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Update
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default EditCustomerModal;
